import * as Sentry from '@sentry/browser';

import { DEFAULT_SENTRY_CONFIG, SENTRY_DSN, SENTRY_PRODUCT, SENTRY_TEAM_NAME } from './constants';
import type { SentryClientConfig } from './types';

export function logException(exception: Error | unknown, location: string) {
	Sentry.withScope((scope) => {
		scope.setTag('location', location);
		scope.setTag('teamName', SENTRY_TEAM_NAME);
		Sentry.captureException(exception, scope);
	});
}

export function setupSentryClient({ sentryConfig }: { sentryConfig?: SentryClientConfig }) {
	// Check for sentry already enabled to be defensive in case two instances are loaded in the same page.
	if (typeof Sentry.getCurrentHub().getClient() !== 'undefined') {
		return;
	}

	const { dsn = SENTRY_DSN, product = SENTRY_PRODUCT, env } = sentryConfig || {};

	const productBuild = getProductBuild();
	const environment = getEnvironment(env);

	Sentry.init({
		...DEFAULT_SENTRY_CONFIG,
		dsn,
		environment,
		beforeSend: (event) => {
			// We only want errors captured explicitly via logException to avoid unwanted errors being sent
			if (event.tags?.teamName === undefined) {
				return null;
			}
			return event;
		},
		initialScope: {
			tags: {
				product,
				productBuild,
			},
		},
		attachStacktrace: true,
	});
}

function getProductBuild() {
	// Ensure we don't invoke window object in SSR
	if (typeof window === 'undefined') {
		return 'unknown';
	}

	switch (true) {
		// Jira - window.BUILD_KEY
		case 'BUILD_KEY' in window:
			return (window.BUILD_KEY as string) || 'unknown';
		// Confluence - window.__buildInfo
		case '__buildInfo' in window:
			return (window.__buildInfo as { FRONTEND_VERSION?: string })?.FRONTEND_VERSION || 'unknown';
		default:
			return 'unknown';
	}
}

function getEnvironment(env?: string) {
	// Ensure we don't invoke window object in SSR
	return typeof window !== 'undefined' && window.location?.href.includes('localhost')
		? 'dev'
		: (env ?? 'prod');
}
